import clsx from 'clsx';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Typography } from '@/components/ui/typography';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

interface TargetHoverCardProps {
  children: React.ReactNode;
  isFetching: boolean;
}

export function TargetHoverCard({
  children,
  isFetching,
}: TargetHoverCardProps) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const {
    hiringTarget: {
      hiringGoalSpend,
      projectedBudget,
      targetBudget,
      preHiringGoalSpend,
      campaignBudget,
      postHiringGoalBudget,
    },
    hasHiringGoal,
  } = useEditCampaignContext();
  const { formatCurrency } = useCurrencySymbol();

  // No hover breakdown without hiring-goal context (includes all-campaigns edit).
  if (!isFetching && !hasHiringGoal) return null;

  return (
    <HoverCard open={open} onOpenChange={setOpen}>
      <HoverCardTrigger>{children}</HoverCardTrigger>
      {!isFetching && (
        <HoverCardContent align="start" className="w-80">
          <div className="flex flex-col items-start justify-center gap-2">
            {/* Input budget */}
            <RowItem
              label={intl.formatMessage({
                defaultMessage: 'Campaign budget',
                description: 'This is the budget for the campaign',
              })}
              value={`${formatCurrency(campaignBudget)}`}
            />

            {/* Pre hiring goal spend */}
            <RowItem
              label={intl.formatMessage({
                defaultMessage: 'Spent before hiring goal',
                description: 'This is the spend before the hiring goal',
              })}
              value={`-${formatCurrency(preHiringGoalSpend)}`}
              color="bg-fx-brand-800"
            />

            {/* Post hiring goal budget */}
            <RowItem
              label={intl.formatMessage({
                defaultMessage: 'Spend planned after hiring goal',
                description: 'This is the spend after the hiring goal',
              })}
              value={`-${formatCurrency(postHiringGoalBudget)}`}
              color="bg-fx-brand-800"
            />

            {/* Target budget */}
            <RowItem
              label={intl.formatMessage({
                defaultMessage: 'Hiring target budget',
                description: 'This is the projected budget for the campaign',
              })}
              value={`${formatCurrency(targetBudget)}`}
              isBold
            />

            {/* Hiring goal spend */}
            <RowItem
              label={intl.formatMessage({
                defaultMessage: 'Spent during hiring goal',
                description: 'This is the spend during the hiring goal',
              })}
              value={`-${formatCurrency(hiringGoalSpend)}`}
              color="bg-fx-brand-600"
            />

            <RowItem
              label={intl.formatMessage({
                defaultMessage: 'Remaining budget',
                description: 'This is the remaining budget for the campaign',
              })}
              value={`${formatCurrency(projectedBudget)}`}
              isBold
            />
          </div>
        </HoverCardContent>
      )}
    </HoverCard>
  );
}

function RowItem({
  color,
  label,
  value,
  isBold,
}: {
  color?: string;
  label: string;
  value: string;
  isBold?: boolean;
}) {
  if (!value) return null;

  return (
    <div
      className={clsx(
        'flex w-full items-center justify-between gap-2 px-2 py-1.5',
        isBold && 'border-fx-neutral-200 border-t',
      )}
    >
      <div className="flex items-center gap-2">
        {color && <div className={clsx('h-2 w-2 rounded-full', color)} />}
        <Typography
          variant="body1"
          className={clsx('!text-fx-neutral-600', isBold && '!font-medium')}
        >
          {label}
        </Typography>
      </div>
      <Typography variant="body2" className="!text-fx-neutral-600">
        {value}
      </Typography>
    </div>
  );
}
